import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  buildAuthorizeUrl,
  buildLogoutUrl,
  defaultRedirectUri,
  exchangeCodeForTokens,
  generatePkcePair,
  randomState,
  refreshTokens,
  type TokenSet,
} from './pkce.js';

/**
 * In-memory auth state for the admin SPA (ADR 0003). Tokens live only in React state/refs — never
 * in localStorage. A silent refresh is scheduled before expiry so a multi-hour gig is not
 * interrupted (§32.3). The PKCE verifier + `state` transit through sessionStorage only for the
 * redirect round-trip and are cleared on completion.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthUser {
  readonly sub: string;
  readonly email: string | null;
}

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Current access token or null; read by the API client per request. */
  getAccessToken: () => string | null;
  /** Begin the Authorization Code + PKCE flow by redirecting to the hosted UI. */
  login: (returnTo?: string) => Promise<void>;
  /** Complete the flow from the /app/callback route. Returns where to navigate next. */
  completeLogin: (search: string) => Promise<string>;
  logout: () => void;
}

const VERIFIER_KEY = 'gigify.pkce.verifier';
const STATE_KEY = 'gigify.pkce.state';
const RETURN_KEY = 'gigify.pkce.returnTo';
/** Refresh this many ms before the token actually expires. */
const REFRESH_SKEW_MS = 60_000;

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeJwtClaims(idToken: string): AuthUser | null {
  try {
    const part = idToken.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { sub?: string; email?: string };
    if (!claims.sub) return null;
    return { sub: claims.sub, email: claims.email ?? null };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('unauthenticated');
  const [user, setUser] = useState<AuthUser | null>(null);
  // Tokens are held in a ref (source of truth for the API client) mirrored to state for reactivity.
  const tokensRef = useRef<TokenSet | null>(null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getAccessToken = useCallback(() => tokensRef.current?.accessToken ?? null, []);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const applyTokens = useCallback(
    (tokens: TokenSet) => {
      tokensRef.current = tokens;
      setUser(decodeJwtClaims(tokens.idToken));
      setStatus('authenticated');
    },
    [],
  );

  const logout = useCallback(() => {
    clearRefreshTimer();
    tokensRef.current = null;
    setUser(null);
    setStatus('unauthenticated');
    if (typeof window !== 'undefined') {
      window.location.assign(buildLogoutUrl(`${window.location.origin}/app`));
    }
  }, [clearRefreshTimer]);

  const scheduleRefresh = useCallback(
    (tokens: TokenSet) => {
      clearRefreshTimer();
      if (!tokens.refreshToken) return;
      const delay = Math.max(0, tokens.expiresAt - Date.now() - REFRESH_SKEW_MS);
      refreshTimer.current = setTimeout(() => {
        void (async () => {
          const rt = tokensRef.current?.refreshToken;
          if (!rt) return;
          try {
            const next = await refreshTokens(rt);
            applyTokens(next);
            scheduleRefresh(next);
          } catch {
            // Refresh failed: drop to unauthenticated so the UI can prompt re-auth (§32.3).
            tokensRef.current = null;
            setUser(null);
            setStatus('unauthenticated');
          }
        })();
      }, delay);
    },
    [applyTokens, clearRefreshTimer],
  );

  const login = useCallback(async (returnTo?: string) => {
    const { verifier, challenge } = await generatePkcePair();
    const state = randomState();
    sessionStorage.setItem(VERIFIER_KEY, verifier);
    sessionStorage.setItem(STATE_KEY, state);
    if (returnTo) sessionStorage.setItem(RETURN_KEY, returnTo);
    window.location.assign(buildAuthorizeUrl({ challenge, state }));
  }, []);

  const completeLogin = useCallback(
    async (search: string): Promise<string> => {
      const params = new URLSearchParams(search);
      const error = params.get('error');
      if (error) throw new Error(params.get('error_description') ?? error);
      const code = params.get('code');
      const returnedState = params.get('state');
      const verifier = sessionStorage.getItem(VERIFIER_KEY);
      const expectedState = sessionStorage.getItem(STATE_KEY);
      const returnTo = sessionStorage.getItem(RETURN_KEY) ?? '/app';
      sessionStorage.removeItem(VERIFIER_KEY);
      sessionStorage.removeItem(STATE_KEY);
      sessionStorage.removeItem(RETURN_KEY);
      if (!code || !verifier) throw new Error('Missing authorization code or PKCE verifier.');
      if (!returnedState || returnedState !== expectedState) {
        throw new Error('Authorization state mismatch — please sign in again.');
      }
      const tokens = await exchangeCodeForTokens(code, verifier, defaultRedirectUri());
      applyTokens(tokens);
      scheduleRefresh(tokens);
      return returnTo;
    },
    [applyTokens, scheduleRefresh],
  );

  useEffect(() => () => clearRefreshTimer(), [clearRefreshTimer]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, getAccessToken, login, completeLogin, logout }),
    [status, user, getAccessToken, login, completeLogin, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
